import { createServerFn } from "@tanstack/react-start";
import { getAuthContext, getOrCreateAccount } from "@/lib/services";
import type { FriendView, SettlementView } from "@/lib/types";
import { listFriends } from "@/db/friend";
import {
  listSettlements,
  getSettlementForUser,
  createSettlement as dbCreateSettlement,
  deleteSettlement as dbDeleteSettlement,
} from "@/db/settlement";
import { createTransfer, getTransfer } from "@/db/pgledger";
import { getUserSettings } from "@/db/user";

export const loadSettlements = createServerFn({ method: "GET" }).handler(async () => {
  const { db, userId, email, name } = await getAuthContext();

  try {
    const [settlementRows, friendRows] = await Promise.all([
      listSettlements(db, userId, null, 50, null),
      listFriends(db, userId),
    ]);

    const settlements: SettlementView[] = settlementRows.map((s) => ({
      id: s.id.toString(),
      fromUserId: s.from_user_id,
      toUserId: s.to_user_id,
      amount: s.amount,
      currency: s.currency,
      date: new Date(s.date).toISOString().split("T")[0],
    }));

    const friends: FriendView[] = friendRows.map((f) => ({
      id: f.id.toString(),
      userId: f.user_id,
      user: { id: f.user_id, email: f.email, name: f.name },
    }));

    return {
      settlements,
      friends,
      currentUser: { id: userId, email, name },
    };
  } catch (error) {
    console.error("Failed to load settlements:", error);
    return {
      settlements: [] as SettlementView[],
      friends: [] as FriendView[],
      currentUser: { id: userId, email, name },
    };
  }
});

export const loadNewSettlement = createServerFn({ method: "GET" }).handler(async () => {
  const { db, userId, email, name } = await getAuthContext();

  try {
    const [friendRows, settings] = await Promise.all([
      listFriends(db, userId),
      getUserSettings(db, userId),
    ]);
    const friends: FriendView[] = friendRows.map((f) => ({
      id: f.id.toString(),
      userId: f.user_id,
      user: { id: f.user_id, email: f.email, name: f.name },
    }));
    return {
      friends,
      currentUser: { id: userId, email, name },
      defaultCurrency: settings.default_currency,
    };
  } catch (error) {
    console.error("Failed to load data:", error);
    return {
      friends: [] as FriendView[],
      currentUser: { id: userId, email, name },
      defaultCurrency: "CAD",
    };
  }
});

export const loadSettlementDetail = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId, email, name } = await getAuthContext();

    const [settlementRow, friendRows] = await Promise.all([
      getSettlementForUser(db, Number(data.id), userId),
      listFriends(db, userId),
    ]);

    if (!settlementRow) {
      throw new Error("Settlement not found");
    }

    const settlement: SettlementView = {
      id: settlementRow.id.toString(),
      fromUserId: settlementRow.from_user_id,
      toUserId: settlementRow.to_user_id,
      amount: settlementRow.amount,
      currency: settlementRow.currency,
      date: new Date(settlementRow.date).toISOString().split("T")[0],
    };

    const friends: FriendView[] = friendRows.map((f) => ({
      id: f.id.toString(),
      userId: f.user_id,
      user: { id: f.user_id, email: f.email, name: f.name },
    }));

    return {
      settlement,
      friends,
      currentUser: { id: userId, email, name },
    };
  });

export const createSettlementFn = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      fromUserId: string;
      toUserId: string;
      amount: string;
      currency: string;
      date: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { db } = await getAuthContext();

    if (data.fromUserId === data.toUserId) {
      throw new Error("Cannot settle with yourself");
    }

    const fromAccount = await getOrCreateAccount(db, data.fromUserId, data.currency);
    const toAccount = await getOrCreateAccount(db, data.toUserId, data.currency);

    // Settlement transfer: FROM to_account TO from_account (reversed direction)
    const transfer = await createTransfer(
      db,
      toAccount.pgledger_account_id,
      fromAccount.pgledger_account_id,
      data.amount,
      null,
      { type: "settlement" },
    );

    await dbCreateSettlement(
      db,
      data.fromUserId,
      data.toUserId,
      data.amount,
      data.currency,
      data.date,
      transfer.id,
    );
  });

export const deleteSettlementFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();

    const settlementId = Number(data.id);

    if (!(await getSettlementForUser(db, settlementId, userId))) {
      throw new Error("Settlement not found");
    }

    const transferId = await dbDeleteSettlement(db, settlementId);

    if (transferId) {
      const original = await getTransfer(db, transferId);
      if (original) {
        await createTransfer(
          db,
          original.to_account_id,
          original.from_account_id,
          original.amount,
          null,
          { reversal_of: transferId, deleted_settlement_id: settlementId },
        );
      }
    }
  });
